import { computed } from 'vue'

import { api } from '@/api/v2/client'
import {
  useAiConversationList,
  useDeleteConversation,
  useRenameConversation,
} from '@/api/v2/queries'
import type { AiMessage } from '@/stores/ai'
import { useAiStore } from '@/stores/ai'

export function useAiConversations() {
  const aiStore = useAiStore()
  const { data, isLoading } = useAiConversationList()
  const renameMutation = useRenameConversation()
  const deleteMutation = useDeleteConversation()

  const conversations = computed(() => data.value?.conversations ?? [])

  async function loadConversation(id: string) {
    aiStore.isLoadingHistory = true
    try {
      const responseData = await api.get<{ messages: unknown[] }>(`/ai/conversations/${id}`)
      const msgs: AiMessage[] = []
      let idx = 0
      for (const m of (responseData as { messages?: unknown[] }).messages ?? []) {
        const raw = m as Record<string, unknown>
        const role = (raw.role as string) ?? 'user'
        const content = raw.content

        if (role === 'user' || (role === 'assistant' && typeof content === 'string')) {
          msgs.push({
            id: String(idx++),
            role: role as AiMessage['role'],
            content: (content as string) ?? '',
            timestamp: (raw.timestamp as string) ?? '',
          })
        } else if (role === 'assistant' && Array.isArray(content)) {
          for (const part of content) {
            if (part.type === 'tool-call') {
              msgs.push({
                id: String(idx++),
                role: 'tool',
                content: '',
                toolName: part.toolName,
                toolInput: part.input,
                timestamp: (raw.timestamp as string) ?? '',
              })
            } else if (part.type === 'text') {
              msgs.push({
                id: String(idx++),
                role: 'assistant',
                content: part.text ?? '',
                timestamp: (raw.timestamp as string) ?? '',
              })
            }
          }
        } else if (role === 'tool' && Array.isArray(content)) {
          for (const part of content) {
            if (part.type === 'tool-result') {
              const output = part.output
              let resultText = ''
              if (typeof output === 'string') resultText = output
              else if (output && typeof output === 'object' && 'value' in output) {
                resultText =
                  typeof output.value === 'string' ? output.value : JSON.stringify(output.value)
              } else if (output != null) {
                resultText = JSON.stringify(output)
              }
              msgs.push({
                id: String(idx++),
                role: 'tool',
                content: resultText,
                toolName: part.toolName,
                toolResult: resultText,
                timestamp: (raw.timestamp as string) ?? '',
              })
            }
          }
        } else {
          msgs.push({
            id: String(idx++),
            role: role as AiMessage['role'],
            content: typeof content === 'string' ? content : JSON.stringify(content),
            timestamp: (raw.timestamp as string) ?? '',
          })
        }
      }

      // Merge tool-result messages into their matching tool-call messages.
      for (let i = msgs.length - 1; i >= 0; i--) {
        const msg = msgs[i]
        if (msg.role === 'tool' && msg.toolResult && !msg.toolInput) {
          const callIdx = msgs
            .slice(0, i)
            .reverse()
            .findIndex(
              m => m.role === 'tool' && m.toolName === msg.toolName && m.toolInput && !m.toolResult,
            )
          if (callIdx >= 0) {
            const actualIdx = i - 1 - callIdx
            msgs[actualIdx].toolResult = msg.toolResult
            msgs.splice(i, 1)
          }
        }
      }

      aiStore.setConversation(id, msgs)
    } finally {
      aiStore.isLoadingHistory = false
    }
  }

  async function renameConversation(id: string, title: string) {
    await renameMutation.mutateAsync({ id, title })
  }

  async function deleteConversation(id: string) {
    await deleteMutation.mutateAsync({ id })
    if (aiStore.currentConversationId === id) {
      aiStore.startNewConversation()
    }
  }

  return {
    conversations,
    isLoading,
    loadConversation,
    renameConversation,
    deleteConversation,
  }
}
